import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { TimeUnit } from 'generated/graphql'
import type { ListTerrotoriesSortFields } from 'generated/graphql'
import { useTerritoryQueries } from 'hooks-queries/territory'
import { PanelStore } from 'stores'
import analyticsTracker from 'utils/analyticsTracker'
import { getNormalizedTerritoriesDataForTerritoriesTable } from 'utils/getNormalizedTerritoriesDataForTerritoriesTable'

import { GET_COUNTRY_THEMES_LOG } from './theme-list-tab.consts'
import type { TThemeListTab } from './theme-list-tab.types'
import { DEFAULT_SORT, DEFAULT_TIME, GLOBAL_TAXONOMY_THEMES_LIMIT } from '../common/global-topic.consts'
import type { TTableOrder } from '../common/global-topic.types'
import { getNextTableOrder, handleSort } from '../common/global-topic.utils'

export const useThemeListTabModel = (): TThemeListTab => {
  const [order, setOrder] = useState<TTableOrder>(DEFAULT_SORT)
  const location = useLocation()
  const workspaceId = Object(PanelStore?.selectedPanel)?.id

  const timeRange = useMemo(() => {
    const dateRangeValue = new URLSearchParams(location.search).get('dateRange')
    return {
      timeUnit: TimeUnit.Month,
      timeValue: Number(dateRangeValue) || DEFAULT_TIME,
    }
  }, [location.search])

  const { data, error, loading, refetch } = useTerritoryQueries({
    queryName: 'getCountryThemes',
    queryVariables: {
      pagination: { take: GLOBAL_TAXONOMY_THEMES_LIMIT },
      timeRange,
    },
    errorLog: GET_COUNTRY_THEMES_LOG,
  })

  const themes = useMemo(() => {
    const normalizedThemes = getNormalizedTerritoriesDataForTerritoriesTable(
      data?.listCountryTerritories?.data,
      timeRange.timeValue,
      workspaceId,
      undefined,
      undefined
    )
    return handleSort(normalizedThemes, order)
  }, [data?.listCountryTerritories?.data, order, timeRange.timeValue, workspaceId])

  const handleSetOrder = (field: ListTerrotoriesSortFields) => {
    setOrder(prevState => {
      const newOrder = getNextTableOrder(prevState, field)

      const { SORT } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(SORT, {
        Page: 'Search Topics Page',
        'Sort field': field,
        'Sort order': newOrder.order,
      })

      return newOrder
    })
  }

  return {
    data: themes,
    error: Boolean(error),
    isLoading: loading,
    refetch: () => refetch(),
    totalCount: data?.listCountryTerritories?.totalCount,
    handleSetOrder,
    order,
  }
}
